import React, { useEffect, useState } from 'react';
import { BaSeIcon } from '@base/react';
import {
  FilterClearButton,
  FilterContainer,
  FlexDirectionWrapper,
  MainContent,
  SubTitle,
} from '../../../../../components/StyledComponents';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Accordion from '@material-ui/core/Accordion';
import { ExcelExportAdmin } from '../../../../../components/ExcelExportAdmin';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
  formatDateToBR,
  formatHoursToBRWithoutSeconds,
} from '../../../../../utils/time';
import { SearchInputAdmin } from '../../../../../components/SearchInputAdmin';
import moment from 'moment';
import { formatCPF, removeEmpty } from '../../../../../utils/utils';
import { cnpjMask, cpfMask, numberLocale } from '../../../../../utils/mask';
import { AccordionDetails } from '@material-ui/core';
import { FiltrosReports } from '../../FiltrosReports';
import filtersModel from '../../../filters.model';
import { TableAdminReports } from '../../../../../components/TableAdmin/TableAdminReports';
import { AgendamentosService } from '../../../services/AgendamentosService';
import { useApp } from '../../../../../context/app';

export const GerenciarAgendamentos = () => {
  const { filtersDashboard, setFiltersDashboard } = useApp();
  const {
    handleOrderClick,
    handleInputPageChange,
    handlePageChange,
    onLinesPerPageChange,
    filters,
    setFilters,
    paginationFilters,
    setPaginationFilters,
    inputSearchValue,
    setInputSearchValue,
    isLoading,
    setIsLoading,
    filterOptions,
    setFilterOptions,
    cleanSelectedFiltersFromModel,

    // handleFiltros
    handleDTInicioChange,
    handleDTFimChange,
    handleMesChange,
    handleAnoChange,
    handleUFChange,
    handleCidadeChange,
    handleUserQueChange,
    handleTipoUserChange,
    handlePorteChange,
    handleSegmentosChange,
    handleCNAEChange,
    handlePrimeiroAcessoChange,
    handleUltimoAcessoChange,
    handleQtdAcessoChange,
    handleAtendentesChange,

    // CONSTANTS
    isCPF,
    isALL,
    isCNPJ,
    cnaesFiltered,
    filtersSelectedValues,
    cidades,
  } = filtersModel();

  const [data, setData] = useState([]);

  const getDataExcel = async () => {
    const bodyParams = removeEmpty({
      ...filters,
      oqueVoceProcura: inputSearchValue,
    });
    const pageFilter = { ...paginationFilters, page: 0, linesPerPage: 1000 };
    const response = await AgendamentosService.findAll(pageFilter, bodyParams);

    return response?.content?.map((xls) => ({
      DATA: moment(xls?.data).format('DD/MM/YYYY'),
      HORA: formatHoursToBRWithoutSeconds(xls?.fim),
      NOME: xls?.administrador?.nome,
      CPF: cpfMask(xls?.administrador?.cpf),
      CNPJ: xls?.administrador?.minhasEmpresas.map((emp) =>
        cnpjMask(emp?.cnpj)
      ),
      UF: xls?.administrador?.uf?.sigla,
      CIDADE: xls?.administrador?.cidade?.nome,
      DATA_AGENDAMENTO: formatDateToBR(xls?.dataAgendamento),
      ATENDENTE: xls?.atendente?.nome,
    }));
  };

  const getData = async (source) => {
    const bodyParams = removeEmpty({
      ...filters,
      oqueVoceProcura: inputSearchValue,
      tipoPessoa: !!isALL
        ? 'TODAS'
        : !!isCPF
        ? 'PESSOA_FISICA'
        : 'PESSOA_JURIDICA',
      ufs: filtersDashboard?.ufs ?? filters?.ufs ?? null,
      cidades: filtersDashboard?.cidades ?? filters?.cidades ?? null,
      dataInicial:
        filtersDashboard?.dataInicial ?? filters?.dataInicial ?? null,
      dataFinal: filtersDashboard?.dataFinal ?? filters?.dataFinal ?? null,
    });
    const response = await AgendamentosService.findAll(
      paginationFilters,
      bodyParams,
      source
    );
    setIsLoading(false);
    setData(response);
  };

  const getFilterOptionsData = async () => {
    setFilterOptions({});
    const response = await AgendamentosService.getFilterOptions();
    setFilterOptions(response);
  };

  const onClickClearFilters = () => {
    setIsLoading(true);
    setData([]);
    setInputSearchValue(null);
    setFiltersDashboard({});
    setFilters({
      status: '',
    });
    setPaginationFilters({
      page: 0,
      linesPerPage: 10,
      orderBy: 'id',
      direction: 'DESC',
    });
    cleanSelectedFiltersFromModel();
  };

  useEffect(() => {
    setIsLoading(true);
    setData([]);
    getData();
    // https://github.com/facebook/create-react-app/issues/6880
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationFilters, filters, inputSearchValue]);

  useEffect(() => {
    getFilterOptionsData();
    // https://github.com/facebook/create-react-app/issues/6880
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableColumns = [
    {
      field: 'dataCadastro',
      id: 'dataCadastro',
      label: 'Data',
      minWidth: 50,
      handleField: formatDateToBR,
    },
    {
      field: 'dataCadastro',
      id: 'dataCadastro',
      label: 'Hora',
      minWidth: 50,
      handleField: formatHoursToBRWithoutSeconds,
    },
    {
      field: 'administrador',
      id: 'administrador',
      subfield: 'nome',
      label: 'Nome',
      minWidth: 100,
    },
    {
      field: 'administrador',
      id: 'administrador',
      subfield: 'cpf',
      label: 'CPF',
      minWidth: 100,
      handleField: formatCPF,
    },
    {
      field: 'administrador',
      id: 'administrador',
      subfield: 'minhasEmpresas',
      subfield2: 'cnpj',
      label: 'CNPJ',
      minWidth: 150,
      handleField: cnpjMask,
    },
    {
      field: 'administrador',
      id: 'administrador',
      subfield: 'uf',
      subfield2: 'sigla',
      label: 'UF',
      minWidth: 50,
    },
    {
      field: 'administrador',
      id: 'administrador',
      subfield: 'cidade',
      subfield2: 'nome',
      label: 'Cidade',
      minWidth: 100,
    },
    {
      field: 'dataAgendamento',
      id: 'dataAgendamento',
      label: 'Data Agendamento',
      minWidth: 100,
      handleField: formatDateToBR,
    },
    {
      field: 'atendente',
      id: 'atendente',
      subfield: 'nome',
      label: 'Atendente',
      minWidth: 100,
    },
  ];

  const renderFilters = (color) => {
    return (
      <FilterContainer>
        <Accordion style={{ backgroundColor: '#F8F8F8', marginBottom: 20 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <BaSeIcon
              name="filter"
              color={color}
              size={25}
              description="Ícone de filtro"
            />
            <p
              style={{ color: color, fontWeight: 'bold', marginTop: '0.2rem' }}
            >
              Filtrar
            </p>
            <FilterClearButton
              color={color}
              onClick={(e) => {
                e.stopPropagation();
                onClickClearFilters();
              }}
            >
              <BaSeIcon
                name="close"
                size={10}
                color={color}
                description="Limpar filtros"
              />
              <p
                style={{
                  fontSize: '12px',
                  color: color,
                  borderBottom: `1px solid ${color}`,
                }}
              >
                Limpar filtros
              </p>
            </FilterClearButton>
          </AccordionSummary>
          <AccordionDetails>
            <FiltrosReports
              typeFilters="agendamentos"
              filterOptions={filterOptions}
              filtersSelectedValues={filtersSelectedValues}
              cidades={cidades}
              isALL={isALL}
              isCPF={isCPF}
              isCNPJ={isCNPJ}
              cnaesFiltered={cnaesFiltered}
              onDataInicioChange={handleDTInicioChange}
              onDataFimChange={handleDTFimChange}
              onMesChange={handleMesChange}
              onAnoChange={handleAnoChange}
              onUFChange={handleUFChange}
              onCidadeChange={handleCidadeChange}
              onUserQueChange={handleUserQueChange}
              onTipoUserChange={handleTipoUserChange}
              onPorteChange={handlePorteChange}
              onSegmentosChange={handleSegmentosChange}
              onCNAEChange={handleCNAEChange}
              onPrimeiroAcessoChange={handlePrimeiroAcessoChange}
              onUltimoAcessoChange={handleUltimoAcessoChange}
              onQtdAcessoChange={handleQtdAcessoChange}
              onAtendenteChange={handleAtendentesChange}
            />
          </AccordionDetails>
        </Accordion>
      </FilterContainer>
    );
  };

  return (
    <MainContent>
      <SubTitle color="#00ccff">
        <>
          <h3>Agendamentos</h3>
          <SearchInputAdmin
            value={inputSearchValue}
            onChange={setInputSearchValue}
            placeholder="Digite o CPF ou CNPJ que deseja buscar..."
            iconColor="#00ccff"
          />
        </>
      </SubTitle>

      {renderFilters('#00ccff')}

      <FlexDirectionWrapper
        style={{ justifyContent: 'space-between', marginTop: '12px' }}
      >
        <div>
          <p style={{ fontWeight: 'bold', color: '#00ccff' }}>
            {numberLocale(data?.totalElements)}{' '}
            <strong className="find-results"> resultados encontrados </strong>
          </p>
        </div>

        <ExcelExportAdmin
          filename={`Exportacao_Agendamentos_${formatDateToBR(new Date())}`}
          handleDataCallback={getDataExcel}
          isCsv
        />
      </FlexDirectionWrapper>

      <TableAdminReports
        data={data}
        columnAction={'actions'}
        paginationFilters={paginationFilters}
        linesPerPageFilters={paginationFilters?.linesPerPage}
        pagination={true}
        totalPages={data?.totalPages}
        columns={tableColumns}
        isLoading={isLoading}
        tableColor={'#00ccff'}
        lineColor={'rgb(248,251,255, 0.9)'}
        onLinesPerPageChange={onLinesPerPageChange}
        onPagePreviousChange={handlePageChange}
        onPageNextChange={handlePageChange}
        handleOrderClick={handleOrderClick}
        onInputPageChange={handleInputPageChange}
        typeFilters="agendamentos"
      />
    </MainContent>
  );
};
